import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiRequests } from 'hooks';
import { UpdateCustomConsentFormPayload } from 'types/teleconsent';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function useFormBuilderAutosave(
  formId: string,
  getValue: () => UpdateCustomConsentFormPayload,
  debounceMs = 1500,
): { saveState: SaveState; schedule: () => void; flush: () => Promise<void> } {
  const { updateCustomConsentForm } = useApiRequests();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (saveState === 'saving' || (timerRef.current !== null && !isSavingRef.current)) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveState]);

  const doSave = useCallback(async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setSaveState('saving');
    try {
      await updateCustomConsentForm(formId, getValue());
      setSaveState('saved');
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
    } finally {
      isSavingRef.current = false;
    }
  }, [formId, getValue, updateCustomConsentForm]);

  const schedule = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSaveState('saving');
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void doSave();
    }, debounceMs);
  }, [doSave, debounceMs]);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await doSave();
  }, [doSave]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  return { saveState, schedule, flush };
}
